import { useEffect, useRef, useState } from "react";
import { LogOut, Search, Settings as SettingsIcon, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { signOut, useSession } from "../lib/authClient";
import { useMe, useMembers } from "../lib/hooks";
import { Avatar } from "./ui/Avatar";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";

export function Topbar() {
  const qc = useQueryClient();
  const session = useSession();
  const { data: me } = useMe();
  const { data: members } = useMembers();
  const { t } = useTranslation();

  // Prefer the real signed-in user; fall back to a demo member so the
  // unauthenticated demo still shows a plausible identity (and no sign-out).
  const demoMember = members?.find((m) => m.role === "advisor") ?? members?.[0];
  const authed = !!session.data && !!me?.user;
  const displayName = me?.user?.name || demoMember?.name || "";
  const displayRole = authed
    ? (me?.memberships[0]?.role ?? "admin")
    : demoMember?.role;
  const displayEmail = me?.user?.email ?? demoMember?.email ?? "";

  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  async function onSignOut() {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      // Clear cached identity/workspace so a later login starts clean. The
      // guard (when VITE_REQUIRE_AUTH is on) then bounces to /login.
      qc.clear();
      window.location.assign("/login");
    }
  }

  return (
    <header className="relative z-30 flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface-0/80 px-4 backdrop-blur">
      <div className="flex-1" />

      <button
        onClick={() => window.dispatchEvent(new Event("biedradar:cmdk"))}
        className="ring-focus hidden items-center gap-2 rounded-md border border-line bg-surface-2 px-3 py-1.5 text-sm text-ink-faint transition-colors hover:bg-surface-3 hover:text-ink-muted sm:flex"
      >
        <Search size={15} />
        <span>{t("topbar.searchDeals")}</span>
        <kbd className="ml-2 rounded border border-line bg-surface-1 px-1.5 py-0.5 text-[10px] text-ink-faint">
          ⌘K
        </kbd>
      </button>

      <LanguageSwitcher />
      <ThemeToggle />

      {displayName && (
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="ring-focus flex items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-surface-2"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar name={displayName} email={displayEmail || null} src={me?.user?.image} size={28} />
            <div className="hidden leading-tight sm:block">
              <div className="text-sm font-medium text-ink">{displayName}</div>
              <div className="text-[11px] capitalize text-ink-faint">
                {displayRole ? t(`roles.${displayRole}`, { defaultValue: displayRole }) : ""}
              </div>
            </div>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="card animate-scale-in absolute right-0 top-full z-50 mt-2 w-56 p-1.5 shadow-pop"
            >
              <div className="px-2.5 py-2">
                <div className="truncate text-sm font-medium text-ink">{displayName}</div>
                {displayEmail && (
                  <div className="truncate text-xs text-ink-faint">{displayEmail}</div>
                )}
              </div>
              <div className="my-1 h-px bg-line" />
              <Link
                to="/app/account"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="ring-focus flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <UserCog size={15} />
                {t("profile.account")}
              </Link>
              <Link
                to="/app/settings"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
                className="ring-focus flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <SettingsIcon size={15} />
                {t("profile.settings")}
              </Link>
              <div className="my-1 h-px bg-line" />
              {authed ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={onSignOut}
                  disabled={signingOut}
                  className="ring-focus flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-50"
                >
                  <LogOut size={15} />
                  {signingOut ? t("common.signingOut") : t("common.signOut")}
                </button>
              ) : (
                <Link
                  to="/login"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                  className="ring-focus flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
                >
                  <LogOut size={15} />
                  {t("common.signIn")}
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
